import {
  LoaderCircle,
  MessageCircle,
  Mic,
  Radio,
  Volume2,
} from "lucide-react";
import { useEffect, useState } from "react";

/** 声を検知したと判定する入力レベル */
const VOICE_DETECT_LEVEL = 0.12;
/** 入力レベルが閾値を下回ってから検知表示を解除するまでの時間 */
const VOICE_HOLD_MS = 1200;

export type QuickInterviewPhase =
  | "ready"
  | "requesting_permission"
  | "connecting"
  | "interviewer_speaking"
  | "candidate_waiting"
  | "candidate_speaking"
  | "candidate_checking"
  | "ending"
  | "error";

const STATUS: Record<
  QuickInterviewPhase,
  { label: string; detail: string; icon: typeof Mic; iconClass: string }
> = {
  ready: {
    label: "開始できます",
    detail: "マイクを使って面接を始めます",
    icon: Mic,
    iconClass: "text-primary",
  },
  requesting_permission: {
    label: "マイクを確認中",
    detail: "ブラウザの許可画面を確認してください",
    icon: LoaderCircle,
    iconClass: "text-primary animate-spin",
  },
  connecting: {
    label: "面接官に接続中",
    detail: "音声セッションを準備しています",
    icon: Radio,
    iconClass: "text-primary animate-pulse",
  },
  interviewer_speaking: {
    label: "面接官が話しています",
    detail: "途中から回答を始めても構いません",
    icon: Volume2,
    iconClass: "text-primary",
  },
  candidate_waiting: {
    label: "あなたの回答を待っています",
    detail: "そのまま話し始めてください",
    icon: Mic,
    iconClass: "text-success",
  },
  candidate_speaking: {
    label: "回答を聞いています",
    detail: "話し終えると次の質問に進みます",
    icon: MessageCircle,
    iconClass: "text-success",
  },
  candidate_checking: {
    label: "聞き取りを確認しています",
    detail: "続けて話す場合はそのまま話してください",
    icon: LoaderCircle,
    iconClass: "text-success animate-spin",
  },
  ending: {
    label: "結果を生成中",
    detail: "会話を保存して評価しています",
    icon: LoaderCircle,
    iconClass: "text-primary animate-spin",
  },
  error: {
    label: "接続できませんでした",
    detail: "内容を確認してもう一度お試しください",
    icon: Radio,
    iconClass: "text-error",
  },
};

/**
 * 入力レベルは毎フレーム更新されるため、閾値をそのまま表示に使うと
 * 発話の合間の谷でラベルが往復してちらつく。閾値を超えた状態を
 * VOICE_HOLD_MS だけ保持して、話し続けている間は表示を固定する。
 */
function useSustainedVoiceDetection(inputLevel: number, enabled: boolean) {
  const isLoud = enabled && inputLevel >= VOICE_DETECT_LEVEL;
  const [held, setHeld] = useState(false);

  if (isLoud && !held) setHeld(true);

  useEffect(() => {
    if (isLoud || !held) return;
    const timer = setTimeout(() => setHeld(false), VOICE_HOLD_MS);
    return () => clearTimeout(timer);
  }, [isLoud, held]);

  return isLoud || held;
}

export default function VoiceConversationStatus({
  phase,
  inputLevel = 0,
  isMuted = false,
}: {
  phase: QuickInterviewPhase;
  inputLevel?: number;
  isMuted?: boolean;
}) {
  const current = STATUS[phase];
  const Icon = current.icon;
  const activeBars = Math.ceil(inputLevel * 4);
  const isListening = ["candidate_waiting", "candidate_speaking"].includes(phase);
  const voiceDetected = useSustainedVoiceDetection(
    inputLevel,
    phase === "candidate_speaking" && !isMuted,
  );
  const label = isMuted
    ? "ミュート中"
    : phase === "candidate_speaking" && voiceDetected
      ? "声を検知しています"
      : isListening
        ? "聞いています"
        : current.label;

  return (
    <div
      className="flex min-h-[68px] items-center gap-3 border-t border-border bg-surface px-4 py-3"
      aria-live="polite"
    >
      <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-surface-container-low">
        <Icon className={current.iconClass} size={20} aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <p className="text-sm font-bold text-on-surface">{label}</p>
        <p className="text-xs leading-5 text-subtext">{current.detail}</p>
      </div>
      {isListening && (
        <div
          className="ml-auto flex h-8 items-end gap-1"
          role="meter"
          aria-label={isMuted ? "マイク入力はミュート中です" : "マイク入力レベル"}
          aria-valuemin={0}
          aria-valuemax={4}
          aria-valuenow={isMuted ? 0 : activeBars}
        >
          {[1, 2, 3, 4].map((bar) => (
            <span
              key={bar}
              className={`w-1.5 rounded-full transition-colors ${
                !isMuted && bar <= activeBars ? "bg-success" : "bg-border"
              }`}
              style={{ height: `${8 + bar * 4}px` }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
